import React, { useCallback, useEffect, useState } from 'react';
import CustomModal from '../../../util_components/custom_modal';
import { get_api_url } from '../../../../utils/urls';
import { get_api, post_api } from '../../../../redux/api_funcs';

// PROD-7828 R1 payment-attributes card; grant-only module, so a 403 on load hides the card entirely.
const FIELD_STYLES = {
  card: {
    background: 'var(--surface-card)',
    border: '1px solid var(--border-default)',
    borderRadius: 8,
    padding: '12px 16px',
    margin: '12px 0'
  },
  label: { fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 },
  value: { fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' },
  fieldLabel: { fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' },
  input: { display: 'block', width: '100%', padding: 7, marginTop: 4 }
};

const PaymentAttributes = ({ teacher_uuid }) => {
  const [data, setData] = useState(null); // { attrs, can_edit }
  const [error, setError] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  const load = useCallback(() => {
    setError('');
    const url = get_api_url(`/v2/cms_user/teacher_payments/teacher_attrs?teacher_uuid=${teacher_uuid}`, false, true);
    get_api(url, true)
      .then((res) => {
        // malformed envelope must surface the error state, not hide the card forever
        if (!res?.data?.data) throw new Error('unexpected response shape');
        setData(res.data.data);
      })
      .catch((e) => {
        if (e?.response?.status === 403) {
          setForbidden(true);
          return;
        }
        console.error('payment_attributes load failed', e);
        setError('Failed to load payment attributes');
      });
  }, [teacher_uuid]);

  useEffect(() => {
    // reset before loading so a teacher switch never shows the previous teacher's attrs
    setData(null);
    setError('');
    setForbidden(false);
    if (teacher_uuid) load();
  }, [teacher_uuid, load]);

  if (!teacher_uuid || forbidden) return null;
  if (error) {
    return (
      <div style={FIELD_STYLES.card}>
        <span style={{ color: 'var(--state-error)', fontSize: 12, marginRight: 10 }}>{error}</span>
        <button
          type="button"
          onClick={load}
          style={{ padding: '3px 10px', border: '1px solid var(--border-default)', background: 'var(--surface-card)', borderRadius: 5, fontSize: 11, cursor: 'pointer', color: 'var(--text-secondary)' }}
        >
          Retry
        </button>
      </div>
    );
  }
  if (!data) {
    return (
      <div style={FIELD_STYLES.card}>
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Loading payment attributes…</span>
      </div>
    );
  }

  const { attrs, can_edit } = data;

  const openEdit = () => {
    setForm({
      is_gst_registered: attrs.is_gst_registered,
      addl_participant_teacher_type: attrs.addl_participant_teacher_type || '',
      training_batch_date: attrs.training_batch_date || '',
      free_pass_balance: String(attrs.free_pass_balance),
      reason: ''
    });
    setModalError('');
    setEditing(true);
  };

  const closeModal = () => {
    if (!saving) setEditing(false);
  };

  // client-side diff — mirrors the backend's no-op semantics so we can skip empty saves
  const formChanged = () => {
    if (Number(form.is_gst_registered) !== attrs.is_gst_registered) return true;
    if ((form.addl_participant_teacher_type || null) !== (attrs.addl_participant_teacher_type || null)) return true;
    if ((form.training_batch_date || '') !== (attrs.training_batch_date || '')) return true;
    if (form.free_pass_balance !== '' && Number(form.free_pass_balance) !== attrs.free_pass_balance) return true;
    return false;
  };

  const save = () => {
    setModalError('');
    if (!formChanged()) {
      setEditing(false);
      return;
    }
    if (form.free_pass_balance !== '') {
      const n = Number(form.free_pass_balance);
      if (!Number.isInteger(n) || n < 0 || n > 12) {
        setModalError('Free Pass balance must be a whole number between 0 and 12.');
        return;
      }
    }
    if (!form.reason.trim()) {
      setModalError('A reason is required — it goes to the payment audit trail.');
      return;
    }
    setSaving(true);
    const payload = {
      teacher_uuid,
      is_gst_registered: Number(form.is_gst_registered),
      training_batch_date: form.training_batch_date,
      reason: form.reason.trim()
    };
    // blank balance = leave unchanged (never coerce '' to 0 — that would silently zero it)
    if (form.free_pass_balance !== '') {
      payload.free_pass_balance = Number(form.free_pass_balance);
    }
    // '' = no change; the tag has no clear semantics once set (backend contract)
    if (form.addl_participant_teacher_type) {
      payload.addl_participant_teacher_type = form.addl_participant_teacher_type;
    }
    const url = get_api_url('/v2/cms_user/teacher_payments/teacher_attrs', false, true);
    post_api(url, payload, true)
      .then(() => {
        setEditing(false);
        load();
      })
      .catch((e) => {
        setModalError(e?.response?.data?.reason || 'Failed to save payment attributes');
      })
      .finally(() => setSaving(false));
  };

  return (
    <div style={FIELD_STYLES.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Payment attributes</span>
        {can_edit ? (
          <button
            type="button"
            onClick={openEdit}
            style={{
              padding: '4px 12px',
              background: 'var(--surface-card)',
              color: 'var(--brand-primary)',
              border: '1px solid var(--brand-primary)',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Edit
          </button>
        ) : null}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        <div>
          <div style={FIELD_STYLES.label}>GST registered</div>
          <div style={FIELD_STYLES.value}>{attrs.is_gst_registered ? 'Yes (GST 5%)' : 'No'}</div>
        </div>
        <div>
          <div style={FIELD_STYLES.label}>Addl-participant type</div>
          <div style={FIELD_STYLES.value}>
            {attrs.addl_participant_teacher_type || (
              <span style={{ color: 'var(--state-warning)' }}>Not set — treated as NEW</span>
            )}
          </div>
        </div>
        <div>
          <div style={FIELD_STYLES.label}>Training batch date</div>
          <div style={FIELD_STYLES.value}>{attrs.training_batch_date || '—'}</div>
        </div>
        <div>
          <div style={FIELD_STYLES.label}>Free Pass balance</div>
          <div style={FIELD_STYLES.value}>{attrs.free_pass_balance}</div>
        </div>
      </div>

      {/* always mounted: CustomModal only detaches its keydown listener on a show true→false flip */}
      <CustomModal show={editing && !!form} close={closeModal}>
        {form ? (
          <div style={{ minWidth: 'min(460px, 86vw)', padding: '4px 6px' }}>
            <h4 style={{ color: 'var(--brand-primary)', fontSize: 14, marginBottom: 12 }}>Edit payment attributes</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 12 }}>
              <label style={FIELD_STYLES.fieldLabel}>
                GST registered
                <select
                  value={form.is_gst_registered}
                  onChange={(e) => setForm({ ...form, is_gst_registered: e.target.value })}
                  style={FIELD_STYLES.input}
                >
                  <option value={0}>No</option>
                  <option value={1}>Yes (GST 5%)</option>
                </select>
              </label>
              <label style={FIELD_STYLES.fieldLabel}>
                Addl-participant type
                <select
                  value={form.addl_participant_teacher_type}
                  onChange={(e) => setForm({ ...form, addl_participant_teacher_type: e.target.value })}
                  style={FIELD_STYLES.input}
                >
                  {/* once a value exists it cannot be un-set (payment logic needs a tag; NULL is only the pre-seed state) */}
                  <option value="" disabled={!!attrs.addl_participant_teacher_type}>
                    Not set (treated as NEW)
                  </option>
                  <option value="NEW">NEW — 10% of session payment per participant</option>
                  <option value="OLD">OLD — fixed by duration</option>
                </select>
              </label>
              <label style={FIELD_STYLES.fieldLabel}>
                Training batch date
                <input
                  type="date"
                  value={form.training_batch_date}
                  onChange={(e) => setForm({ ...form, training_batch_date: e.target.value })}
                  style={FIELD_STYLES.input}
                />
              </label>
              <label style={FIELD_STYLES.fieldLabel}>
                Free Pass balance (0–12)
                <input
                  type="number"
                  min={0}
                  max={12}
                  step={1}
                  value={form.free_pass_balance}
                  onChange={(e) => setForm({ ...form, free_pass_balance: e.target.value })}
                  style={FIELD_STYLES.input}
                />
              </label>
            </div>
            <label style={FIELD_STYLES.fieldLabel}>
              Reason <span style={{ color: 'var(--state-error)' }}>*</span> (audit trail)
              <textarea
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="e.g. GST certificate received / seeded from Ops sheet"
                style={{ ...FIELD_STYLES.input, minHeight: 56 }}
              />
            </label>
            {modalError ? (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--state-error)' }}>{modalError}</div>
            ) : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                style={{ padding: '6px 14px', border: '1px solid var(--border-default)', background: 'var(--surface-card)', borderRadius: 6, cursor: saving ? 'not-allowed' : 'pointer', color: 'var(--text-secondary)' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                style={{ padding: '6px 14px', border: 'none', background: 'var(--brand-primary)', color: 'var(--text-inverse)', borderRadius: 6, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        ) : null}
      </CustomModal>
    </div>
  );
};

export default PaymentAttributes;
